import { AlertTriangle, Loader2, Mail, Send, Users } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';

/** Rendered sample email returned by POST /jobs/:jobId/broadcast/preview. */
export interface BroadcastPreviewEmail {
  subject: string;
  html: string;
  text: string;
  sampleRecipientName: string;
}

export interface BroadcastPreviewData {
  count: number;
  kolNames: string[];
  email: BroadcastPreviewEmail | null;
}

interface BroadcastConfirmModalProps {
  isOpen: boolean;
  /** How many KOLs the send will reach (server count when known, selection size otherwise). */
  count: number;
  /** Names of the recipients, as far as they are known. May be shorter than `count`. */
  kolNames: string[];
  /** The rendered sample email, or null when it could not be produced. */
  email: BroadcastPreviewEmail | null;
  /** The preview request is still in flight. */
  loading: boolean;
  /** Why the email preview is missing. Never blocks the send. */
  previewUnavailableReason: string | null;
  /** The broadcast POST is in flight. */
  sending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const NAME_PREVIEW_LIMIT = 8;

export default function BroadcastConfirmModal({
  isOpen,
  count,
  kolNames,
  email,
  loading,
  previewUnavailableReason,
  sending,
  onCancel,
  onConfirm,
}: BroadcastConfirmModalProps) {
  const shownNames = kolNames.slice(0, NAME_PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, count - shownNames.length);
  const plural = count === 1 ? '' : 's';
  const sampleName = email?.sampleRecipientName?.trim() || 'this recipient';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!sending) onCancel();
      }}
      title="Review before sending"
      size="xl"
      mobilePresentation="fullscreen"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="outline" onClick={onCancel} disabled={sending} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={sending || count === 0} className="w-full gap-2 sm:w-auto">
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            {sending ? 'Sending…' : `Send ${count} invitation${plural}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Who gets this */}
        <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">
          <div className="flex items-start gap-3">
            <Users className="mt-0.5 h-5 w-5 shrink-0 text-accentText md:text-accent2" aria-hidden />
            <div className="min-w-0">
              <h3 className="font-semibold text-accentText md:text-accent2">
                {count} KOL{plural} will be invited
              </h3>
              {shownNames.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {shownNames.map((name, index) => (
                    <span
                      key={`${name}-${index}`}
                      className="rounded-full border border-glass bg-glass-3 px-2 py-0.5 text-xs text-glass-text-2"
                    >
                      {name}
                    </span>
                  ))}
                  {hiddenCount > 0 && (
                    <span className="rounded-full border border-glass bg-glass-3 px-2 py-0.5 text-xs text-glass-text-3">
                      +{hiddenCount} more
                    </span>
                  )}
                </div>
              )}
              <p className="mt-2 text-sm text-glass-text-2">
                Each recipient gets the email addressed to their own name, with their own invitation link.
              </p>
            </div>
          </div>
        </div>

        {/* The email itself */}
        <div className="rounded-xl border border-glass bg-glass-2">
          <div className="flex flex-wrap items-start gap-2 border-b border-glass p-3 md:p-4">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-glass-text-2" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-glass-text-3">Subject</div>
              <div className="truncate text-sm font-semibold text-glass-text">
                {email ? email.subject : 'Job invitation'}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-glass-text-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading the email preview…
            </div>
          ) : email ? (
            <div className="p-3 md:p-4">
              <p className="mb-2 text-xs text-glass-text-3">
                Shown exactly as <span className="font-semibold text-glass-text-2">{sampleName}</span> will
                receive it.
              </p>
              <div className="glass-scroll h-[300px] overflow-auto rounded-lg border border-glass bg-white md:h-[420px]">
                <iframe
                  title={`Invitation email preview for ${sampleName}`}
                  srcDoc={email.html}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  className="block h-full w-full border-0 bg-white"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-4 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-300" aria-hidden />
              <div className="text-glass-text-2">
                <p className="font-medium text-glass-text">Email preview unavailable.</p>
                <p className="mt-1">
                  {previewUnavailableReason ||
                    'The sample could not be rendered right now. You can still send the invitations.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
